import type { Subscription, User } from '@whispers/sdk';
import { useEffect, useMemo, useState } from 'react';

import { useInfiniteUsers } from '../api.js';
import { UserTable } from '../components/admin/UserTable.js';

export const AdminUsersPage = () => {
  const [inputEmail, setInputEmail] = useState('');
  const [debouncedEmail, setDebouncedEmail] = useState('');

  // Debounce: only update the query email 400ms after the user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedEmail(inputEmail);
    }, 400);
    return () => {
      clearTimeout(timer);
    };
  }, [inputEmail]);

  const { data, isLoading, isError, error, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteUsers({
      include: 'subscriptions',
      ...(debouncedEmail && { email: debouncedEmail }),
      pageSize: 25,
    });

  const displayedUsers: User[] = useMemo(() => data?.pages.flatMap((p) => p.users) ?? [], [data]);

  const subscriptionsByUserId: Record<string, Subscription[]> = useMemo(
    () =>
      (data?.pages.flatMap((p) => p.subscriptions ?? []) ?? []).reduce<
        Record<string, Subscription[]>
      >((acc, sub) => {
        if (!acc[sub.userId]) acc[sub.userId] = [];
        acc[sub.userId]!.push(sub);
        return acc;
      }, {}),
    [data],
  );

  if (isError) {
    return (
      <div className="py-20 text-center">
        <p className="text-sm text-error">
          {error instanceof Error ? error.message : 'Failed to load users. Please try again.'}
        </p>
      </div>
    );
  }

  return (
    <div className="grid gap-6">
      <h1 className="text-xl font-medium text-text">Users</h1>

      <UserTable
        users={displayedUsers}
        subscriptionsByUserId={subscriptionsByUserId}
        isLoading={isLoading && displayedUsers.length === 0}
        hasNextPage={hasNextPage}
        onLoadMore={() => void fetchNextPage()}
        isLoadingMore={isFetchingNextPage}
        emailFilter={inputEmail}
        onEmailFilterChange={setInputEmail}
      />
    </div>
  );
};
